import { AxiosResponse } from 'axios';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'libs/Axios';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { CUBE_INSIGHT_VIEW_QUERY_KEY } from 'services/v1/Tenant/AROCube/CubeInsightViewService';

export const CUBE_INSIGHT_VIEW_CELL_COLOR_QUERY_KEY = {
  EFFECTIVE_CELL_COLORS: 'CUBE_INSIGHT_VIEW_EFFECTIVE_CELL_COLORS',
};

export type CellColorSource = 'SYSTEM' | 'TENANT_OVERRIDE' | 'TENANT_DISABLED';

export type CellColorOverrideState = 'OVERRIDE' | 'DISABLED';

export interface EffectiveCellColorRuleItem {
  id: string | null;
  value: number | null;
  minValue: number | null;
  maxValue: number | null;
  includeMin: boolean | null;
  includeMax: boolean | null;
  priorityOrder: number;
  condition: AutoCompleteItem | null;
  color: AutoCompleteItem | null;
  attributeReference: AutoCompleteItem | null;
}

export interface EffectiveCellColorItem {
  id: string;
  systemCellColorId: string | null;
  overrideId: string | null;
  cubeAttribute: AutoCompleteItem;
  rules: EffectiveCellColorRuleItem[];
  source: CellColorSource;
}

export interface RequestUpsertCellColorOverride {
  overrideState: CellColorOverrideState;
  rules?: EffectiveCellColorRuleItem[];
}

export function useGetEffectiveCellColors(insightViewId?: string) {
  return useQuery<AxiosResponse<EffectiveCellColorItem[]>, AxiosDefaultErrorEntity>(
    [CUBE_INSIGHT_VIEW_CELL_COLOR_QUERY_KEY.EFFECTIVE_CELL_COLORS, insightViewId],
    () => axios.get(`/api/v1/tenant/insight-view/cell-colors/${insightViewId}/effective`).then((res) => res),
    {
      enabled: !!insightViewId,
    },
  );
}

export function useUpsertCellColorOverride(insightViewId?: string) {
  const queryClient = useQueryClient();
  return useMutation<
    AxiosResponse<EffectiveCellColorItem>,
    AxiosDefaultErrorEntity,
    { cubeAttributeId: string; body: RequestUpsertCellColorOverride }
  >(
    ({ cubeAttributeId, body }) =>
      axios
        .put(`/api/v1/tenant/insight-view/cell-colors/${insightViewId}/${cubeAttributeId}/override`, body)
        .then((res) => res),
    {
      onSuccess: () => {
        // Colours changed: refetch all currently displayed insight view data.
        queryClient.invalidateQueries([CUBE_INSIGHT_VIEW_QUERY_KEY.CUBE_INSIGHT_VIEW_DATA]);
      },
      onSettled: () => {
        queryClient.invalidateQueries([
          CUBE_INSIGHT_VIEW_CELL_COLOR_QUERY_KEY.EFFECTIVE_CELL_COLORS,
          insightViewId,
        ]);
      },
    },
  );
}

export function useDeleteCellColorOverride(insightViewId?: string) {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosDefaultErrorEntity, { cubeAttributeId: string }>(
    ({ cubeAttributeId }) =>
      axios
        .delete(`/api/v1/tenant/insight-view/cell-colors/${insightViewId}/${cubeAttributeId}/override`)
        .then((res) => res),
    {
      onSuccess: () => {
        // Colours reverted to system: refetch all currently displayed insight view data.
        queryClient.invalidateQueries([CUBE_INSIGHT_VIEW_QUERY_KEY.CUBE_INSIGHT_VIEW_DATA]);
      },
      onSettled: () => {
        queryClient.invalidateQueries([
          CUBE_INSIGHT_VIEW_CELL_COLOR_QUERY_KEY.EFFECTIVE_CELL_COLORS,
          insightViewId,
        ]);
      },
    },
  );
}
